import { useState } from 'react';
import styles from './WordListManager.module.css';
import type { SavedWordListWithKey } from '../hooks/useWordLists';
import type { WordList } from '../types';

const GRADE_COLORS: Record<string, string> = {
  'K-2': 'badge-green',
  '3-5': 'badge-blue',
  '6-8': 'badge-purple',
  '9-12': 'badge-orange',
  'College/Adult': 'badge-red',
};

interface WordListManagerProps {
  lists: SavedWordListWithKey[];
  onLoad: (list: WordList) => void;
  onDelete: (key: string) => void;
  onUpdateTitle: (oldKey: string, newTitle: string) => string;
}

export default function WordListManager({ lists, onLoad, onDelete, onUpdateTitle }: WordListManagerProps) {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const startEditing = (key: string, currentTitle: string) => {
    setEditingKey(key);
    setEditValue(currentTitle);
  };

  const saveEdit = () => {
    if (editingKey && editValue.trim()) {
      const newKey = onUpdateTitle(editingKey, editValue.trim());
      // If the key changed and this was the expanded item, update expanded key
      if (expandedKey === editingKey) {
        setExpandedKey(newKey);
      }
    }
    setEditingKey(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  if (lists.length === 0) {
    return (
      <div className={styles.wlmEmpty}>
        <span className="wlm-empty-icon">📚</span>
        <p>No saved word lists yet.</p>
        <p>
          <small>
            After selecting vocabulary words, click "Save Word List" to store them here.
          </small>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wlmContainer}>
      <h3>Saved Word Lists</h3>
      <p className="wlm-subtitle">
        {lists.length} saved {lists.length === 1 ? 'list' : 'lists'} — stored locally in your
        browser
      </p>

      <div className={styles.wlmList}>
        {lists.map((list) => {
          const isExpanded = expandedKey === list.key;
          const gradeColor = GRADE_COLORS[list.gradeLevel] || 'badge-blue';

          return (
            <div key={list.key} className={styles.wlmItem}>
              <div className={styles.wlmItemHeader}>
                <div className={styles.wlmItemInfo}>
                  {editingKey === list.key ? (
                    <input
                      type="text"
                      className={styles.wlmTitleInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={saveEdit}
                      onKeyDown={handleKeyDown}
                      ref={(input) => {
                        if (input) {
                          input.focus();
                          input.select();
                        }
                      }}
                    />
                  ) : (
                    <span
                      className={styles.wlmTitle}
                      onClick={() => startEditing(list.key, list.title)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          startEditing(list.key, list.title);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Click to edit title"
                    >
                      {list.title}
                    </span>
                  )}
                  {list.author && <span className={styles.wlmAuthor}>by {list.author}</span>}
                  <div className={styles.wlmBadges}>
                    <span className={`badge ${gradeColor}`}>{list.gradeLevel}</span>
                    <span className="badge badge-gray">{list.subject}</span>
                    <span className="badge badge-gray">{list.words?.length || 0} words</span>
                  </div>
                  {list.updatedAt && (
                    <span className={styles.wlmDate}>
                      Updated {new Date(list.updatedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <div className={styles.wlmActions}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setExpandedKey(isExpanded ? null : list.key)}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? 'Hide words' : 'Preview'}
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                      onLoad({
                        title: list.title,
                        author: list.author,
                        gradeLevel: list.gradeLevel,
                        subject: list.subject,
                        words: list.words,
                        timestamp: list.updatedAt,
                      })
                    }
                    title="Load this word list for artifact generation"
                  >
                    Use List
                  </button>
                  {confirmDelete === list.key ? (
                    <>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          onDelete(list.key);
                          setConfirmDelete(null);
                        }}
                      >
                        Confirm Delete
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setConfirmDelete(list.key)}
                      title="Delete this list"
                      aria-label="Delete word list"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && list.words?.length > 0 && (
                <div className={styles.wlmWords}>
                  {list.words.map((word, i) => (
                    <div key={i} className={styles.wlmWord}>
                      <span className={styles.wlmWordTerm}>{word.word}</span>
                      {word.partOfSpeech && (
                        <span className={styles.wlmWordPos}>{word.partOfSpeech}</span>
                      )}
                      {word.etymologyRoots && (
                        <span className={styles.wlmWordRoot}>⟨{word.etymologyRoots}⟩</span>
                      )}
                      <span className={styles.wlmWordDef}>{word.definition}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
